import { api } from './client'
import type { ApiEnvelope, PaginatedResponse, Floorplan } from '@/types/api'

export interface FloorplanQueryParams {
    keySearch?: string
    DiagramId?: string
    status?: number
    fromDate?: string
    toDate?: string
    unitId?: string
}

interface SpringPage<T> {
    content: T[]
    totalElements: number
    totalPages: number
    number: number
    size: number
}

function adaptPage<T>(
    raw: SpringPage<T> | PaginatedResponse<T>,
): PaginatedResponse<T> {
    if ('items' in raw) return raw
    return {
        items: raw.content,
        total: raw.totalElements,
        page: raw.number + 1,
        limit: raw.size,
        totalPages: raw.totalPages,
    }
}

/**
 * Fetch list of floorplans with filters and pagination
 */
export async function fetchFloorplans(
    page: number = 0,
    size: number = 10,
    params: FloorplanQueryParams = {},
    signal?: AbortSignal
): Promise<PaginatedResponse<Floorplan>> {
    try {
        const response = await api.get<ApiEnvelope<SpringPage<Floorplan>>>('/v1/diagram-management', {
            params: { page, size, ...params },
            signal
        })
        return adaptPage(response.data.responseData)
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy danh sách sơ đồ.';
        throw new Error(message);
    }
}

export async function getFloorplanById(id: string, signal?: AbortSignal): Promise<Floorplan> {
    try {
        const response = await api.get<ApiEnvelope<Floorplan>>(`/v1/diagram-management/${id}`, { signal })
        return response.data.responseData
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy chi tiết sơ đồ.';
        throw new Error(message);
    }
}

/**
 * Create a new floorplan
 */
export async function createFloorplan(
    data: Omit<Floorplan, 'id' | 'createdAt' | 'updatedAt'>
): Promise<ApiEnvelope<Floorplan>> {
    try {
        const response = await api.post<ApiEnvelope<Floorplan>>('/v1/diagram-management', data)
        return response.data
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi tạo sơ đồ.';
        throw new Error(message);
    }
}

/**
 * Update floorplan
 */
export async function updateFloorplan(
    id: string,
    data: Partial<Omit<Floorplan, 'id' | 'createdAt'>>,
): Promise<ApiEnvelope<Floorplan>> {
    try {
        const response = await api.put<ApiEnvelope<Floorplan>>(`/v1/diagram-management/${id}`, data)
        return response.data
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi cập nhật sơ đồ.';
        throw new Error(message);
    }
}

/**
 * Delete floorplan
 */
export async function deleteFloorplan(id: string): Promise<ApiEnvelope<any>> {
    try {
        const response = await api.delete<ApiEnvelope<any>>(`/v1/diagram-management/${id}`)
        return response.data
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi xóa sơ đồ.';
        throw new Error(message);
    }
}